import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  Box,
  CircularProgress
} from '@mui/material';
import {
  useSearchMarkets,
  useManualPair,
  CachedMarketResult
} from '../api/usePairMapper';

interface Props {
  open: boolean;
  onClose: () => void;
}

export const ManualPairDialog = ({ open, onClose }: Props) => {
  const [polySearch, setPolySearch] = useState('');
  const [kalshiSearch, setKalshiSearch] = useState('');
  const [selectedPoly, setSelectedPoly] = useState<CachedMarketResult | null>(
    null
  );
  const [selectedKalshi, setSelectedKalshi] =
    useState<CachedMarketResult | null>(null);

  const { data: polyResults, isLoading: polyLoading } = useSearchMarkets(
    polySearch,
    'polymarket',
    open
  );
  const { data: kalshiResults, isLoading: kalshiLoading } = useSearchMarkets(
    kalshiSearch,
    'kalshi',
    open
  );
  const manualPairMutation = useManualPair();

  const handlePair = async () => {
    if (!selectedPoly || !selectedKalshi) return;
    await manualPairMutation.mutateAsync({
      polymarketId: selectedPoly.externalId,
      kalshiId: selectedKalshi.externalId
    });
    setSelectedPoly(null);
    setSelectedKalshi(null);
    setPolySearch('');
    setKalshiSearch('');
    onClose();
  };

  const handleClose = () => {
    setSelectedPoly(null);
    setSelectedKalshi(null);
    setPolySearch('');
    setKalshiSearch('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Manual Market Pairing</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={3} mt={1}>
          {/* Polymarket side */}
          <Box flex={1}>
            <Typography variant="subtitle2" mb={1}>
              Polymarket
            </Typography>
            {selectedPoly ? (
              <SelectedMarket
                market={selectedPoly}
                onClear={() => setSelectedPoly(null)}
              />
            ) : (
              <>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search Polymarket..."
                  value={polySearch}
                  onChange={e => setPolySearch(e.target.value)}
                  autoFocus
                />
                <MarketList
                  results={polyResults}
                  isLoading={polyLoading}
                  onSelect={setSelectedPoly}
                />
              </>
            )}
          </Box>

          {/* Kalshi side */}
          <Box flex={1}>
            <Typography variant="subtitle2" mb={1}>
              Kalshi
            </Typography>
            {selectedKalshi ? (
              <SelectedMarket
                market={selectedKalshi}
                onClear={() => setSelectedKalshi(null)}
              />
            ) : (
              <>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search Kalshi..."
                  value={kalshiSearch}
                  onChange={e => setKalshiSearch(e.target.value)}
                />
                <MarketList
                  results={kalshiResults}
                  isLoading={kalshiLoading}
                  onSelect={setSelectedKalshi}
                />
              </>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handlePair}
          disabled={
            !selectedPoly || !selectedKalshi || manualPairMutation.isLoading
          }
        >
          {manualPairMutation.isLoading ? 'Pairing...' : 'Create Pair'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const MarketList = ({
  results,
  isLoading,
  onSelect
}: {
  results?: CachedMarketResult[];
  isLoading: boolean;
  onSelect: (m: CachedMarketResult) => void;
}) => (
  <List
    dense
    sx={{
      maxHeight: 350,
      overflow: 'auto',
      mt: 1,
      bgcolor: 'action.hover',
      borderRadius: 1
    }}
  >
    {isLoading && (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={20} />
      </Box>
    )}
    {results?.map(m => (
      <ListItemButton
        key={m._id}
        onClick={() => onSelect(m)}
        disabled={m.isPaired}
        sx={{ opacity: m.isPaired ? 0.5 : 1 }}
      >
        <ListItemText
          primary={m.title}
          secondary={
            <>
              {m.eventTitle !== m.title && m.eventTitle}
              {m.isPaired && ' (already paired)'}
            </>
          }
          primaryTypographyProps={{ variant: 'body2' }}
          secondaryTypographyProps={{ variant: 'caption' }}
        />
      </ListItemButton>
    ))}
    {results && results.length === 0 && (
      <Typography variant="caption" color="text.secondary" px={2} py={1}>
        No results
      </Typography>
    )}
  </List>
);

const SelectedMarket = ({
  market,
  onClear
}: {
  market: CachedMarketResult;
  onClear: () => void;
}) => (
  <Box
    sx={{
      p: 1.5,
      border: 1,
      borderColor: 'primary.main',
      borderRadius: 1,
      bgcolor: 'action.selected'
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="start">
      <Box>
        <Typography variant="body2" fontWeight={600}>
          {market.title}
        </Typography>
        {market.eventTitle !== market.title && (
          <Typography variant="caption" color="text.secondary">
            {market.eventTitle}
          </Typography>
        )}
        <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
          {market.keywords?.slice(0, 8).map(kw => (
            <Chip key={kw} label={kw} size="small" variant="outlined" />
          ))}
        </Stack>
      </Box>
      <Button size="small" onClick={onClear}>
        Clear
      </Button>
    </Stack>
  </Box>
);
